import {
  MilestoneApproversView,
  MilestonePanelRelease,
  MilestoneRoleDisplay,
  ProjectLeaderRoleDisplay,
  UserSelect,
} from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import {
  GUTTER_SIZE,
  ProjectMilestoneStatus,
  ProjectUserPermissions,
} from "@/enums";
import { projectMilestoneType } from "@/propTypes";
import { useProjectContext } from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { Button, Form, Row, Typography } from "antd";
import { bool } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { getAdditionalApproverIds } from "./helper";

export const MilestoneApproversCard = ({ milestone, skuRequired }) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [regions, setRegions] = useState();

  const { updateMilestone, updatingMilestone, canUser } = useProjectContext();

  const { data: forms } = useGetFormLookupsQuery();

  useEffect(() => {
    setRegions(forms?.intake?.region);
  }, [forms?.intake]);

  const renderAddUsers = () => (
    <>
      <Typography.Title level={4}>
        {intl.formatMessage({
          id: "project.approvers.additionalApproversHeader",
        })}
      </Typography.Title>
      <Form.Item
        label={intl.formatMessage({
          id: "project.approvers.addApproverField",
        })}
        name="additionalApprovers"
        initialValue={getAdditionalApproverIds(milestone.approvers)}
      >
        <UserSelect
          users={getAdditionalApproverIds(milestone.approvers)}
          readOnly={!canUser(ProjectUserPermissions.EditApprovers, milestone)}
        />
      </Form.Item>
    </>
  );

  const renderApprovers = () => {
    const regions = forms.intake?.region;
    return Object.keys(regions).map((region) => {
      const approverRoles = milestone.approvers?.filter(
        (x) => x.regionId === region
      );
      const regionLeaderApprover = milestone.projectLeaderApprovers?.find(
        (x) => x.regionId === region
      );

      if (!approverRoles?.length && !regionLeaderApprover) return null;

      return (
        <S.RegionDisplayCol flex="1" key={regions[region].fallback}>
          <Typography.Title level={4}>
            {(regions || {})[region]?.fallback}
          </Typography.Title>
          <MilestoneRoleDisplay
            region={region}
            roles={approverRoles}
            form={form}
            editMode={
              milestone.approvalRequired &&
              canUser(ProjectUserPermissions.EditApprovers, milestone)
            }
          />
          {regionLeaderApprover && (
            <ProjectLeaderRoleDisplay role={regionLeaderApprover} form={form} />
          )}
        </S.RegionDisplayCol>
      );
    });
  };

  const handleSave = () => {
    const formValues = form.getFieldsValue(true);
    const additionalApproversRole = {
      assignedUsers: formValues.additionalApprovers?.map((userId) => ({
        userId: userId,
      })),
    };
    const approvers = (milestone.approvers ?? [])
      .filter((x) => x.regionId)
      .map((approver) => {
        if (!formValues[approver.id]) return approver;

        return {
          ...approver,
          isNotApplicable:
            formValues[approver.id].isNotApplicable ?? approver.isNotApplicable,
          assignedUsers:
            formValues[approver.id].assignedUsers?.map((x) => ({
              userId: x,
            })) || [],
        };
      });

    const leaderApprovers = milestone.projectLeaderApprovers?.map(
      (approver) => {
        if (!formValues[approver.id]) return approver;

        return {
          ...approver,
          isNotApplicable:
            formValues[approver.id].isNotApplicable ?? approver.isNotApplicable,
        };
      }
    );

    const updatedMilestone = {
      ...milestone,
      approvers: [...approvers, additionalApproversRole],
      projectLeaderApprovers: leaderApprovers,
    };

    updateMilestone(updatedMilestone);
  };

  if (!regions) {
    return <Form form={form} component={false} />;
  }

  if (
    milestone.status !== ProjectMilestoneStatus.Draft &&
    milestone.status !== ProjectMilestoneStatus.Rejected
  ) {
    return (
      <>
        <Form form={form} component={false} />
        <MilestoneApproversView milestone={milestone} />
      </>
    );
  }

  return (
    <>
      <Form form={form} layout="vertical" autoComplete="off">
        <S.MilestoneDetailCard
          styles={{ body: { padding: 0 } }}
          extra={
            milestone.approvalRequired &&
            canUser(ProjectUserPermissions.EditApprovers, milestone) && (
              <Button
                data-test-id="save-milestone-approvers"
                onClick={handleSave}
                type="primary"
                loading={updatingMilestone}
              >
                {intl.formatMessage({ id: "general.save" })}
              </Button>
            )
          }
        >
          <MilestonePanelRelease
            milestone={milestone}
            skuRequired={skuRequired}
          />
          <Row gutter={GUTTER_SIZE.large}>
            {renderApprovers()}
            <S.RegionDisplayCol>
              {milestone.approvalRequired && renderAddUsers()}
            </S.RegionDisplayCol>
          </Row>
        </S.MilestoneDetailCard>
      </Form>
    </>
  );
};

MilestoneApproversCard.propTypes = {
  milestone: projectMilestoneType.isRequired,
  skuRequired: bool,
};
